import PropTypes from "prop-types";
import React, { useEffect, useMemo } from "react";

// Default sections
import { ComplexCommissionTier } from "@/PayPlans/helpers/DefaultSections";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useValidations } from "@/Common/hooks/Validations";

// Custom components
import MatrixRow from "./MatrixRow";

// Tier components
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";
import TierRows from "@/Layouts/components/Tiers/TierRows";
import TierGrossHeader from "@/PayPlans/components/Defaults/Advisors/TierGrossHeader";

// MUI
import { Collapse, Grid, Stack, Typography } from "@mui/material";
import { TransitionGroup } from "react-transition-group";

// Constants
const MAX_INPUT_VALUE = 1000000;

const getCommissionsInput = (tiers = []) => tiers?.[0]?.["commissions"]?.[0]?.["commission"]?.["input"] || "Dollar";
const getTierReferenceInput = (tier) => tier?.["commission"]?.["input"] || "Dollar";
export default function TiersPayment(props) {
	const { title = "", tiers = [], readOnly = false, onActionUpdate = () => {}, onActionValidation = () => {} } = props;

	// Hooks
	const { errors, isValid, getErrorsMessages, validate } = useValidations();
	const localErrorsMessages = getErrorsMessages();

	// Errors
	const rowsErrors = errors?.get("gross");
	const columnsErrors = errors?.get("commissions");

	// Control to send update actions
	const wasUpdated = React.useRef(false);

	const onTiersUpdate = (updatedTiers) => {
		onActionValidation?.(isValid);
		if (wasUpdated.current) {
			wasUpdated.current = false;
			onActionUpdate?.(updatedTiers);
		}
	};

	const [innerTiers, setInnerTiers] = useCallbackState([], onTiersUpdate);

	// Methods
	const updatePayments = (updatedTiers = [], updatedPayments = []) =>
		updatedTiers.map((tier) => ({
			...tier,
			commissions: tier.commissions.map((item, key) => ({ ...item, payment: updatedPayments?.[key] })),
		}));

	const handleAddColumn = (updatedHeaders = []) => {
		let updatedTiers = [...innerTiers];
		const lastColumnIndex = updatedTiers?.[0]?.["commissions"]?.length;
		const headersLength = updatedHeaders.length;
		const isAdding = lastColumnIndex !== headersLength;

		if (isAdding) {
			const commissionInput = getCommissionsInput(updatedTiers);
			updatedTiers.forEach((tier) => {
				tier.commissions.push({
					payment: updatedHeaders?.[lastColumnIndex],
					commission: { input: commissionInput, value: 0 },
				});
			});
		} else {
			updatedTiers = updatePayments(updatedTiers, updatedHeaders);
		}

		wasUpdated.current = true;
		setInnerTiers(updatedTiers);
	};

	const handleDeleteColumn = (updatedHeaders = []) => {
		let updatedTiers = [...innerTiers];
		if (updatedTiers?.[0]?.["commissions"]?.length > 1) {
			updatedTiers.forEach((tier) => {
				tier.commissions.splice(-1);
			});
			updatedTiers = updatePayments(updatedTiers, updatedHeaders);
		}
		update(updatedTiers);
	};

	const handleAddTier = () => {
		const updatedTiers = [...innerTiers];
		const lastIndex = updatedTiers?.length ? updatedTiers.length - 1 : 1;
		const commissions = [...(updatedTiers?.[0]?.commissions || [])].map((item) => {
			const commissionInput = getTierReferenceInput(item);
			return {
				...item,
				commission: { value: 0, input: commissionInput },
			};
		});

		updatedTiers.push(ComplexCommissionTier([0, "Dollar"], [MAX_INPUT_VALUE, "Dollar"], { commissions }));

		if (updatedTiers?.length) {
			updatedTiers[lastIndex].to.value = 0;
		}

		wasUpdated.current = true;
		setInnerTiers(updatedTiers);
	};

	const handleDeleteTier = () => {
		const updatedTiers = [...innerTiers];
		if (updatedTiers.length >= 1) {
			updatedTiers.splice(-1);
		}
		const lastIndex = updatedTiers?.length ? updatedTiers.length - 1 : 1;
		updatedTiers[lastIndex].to.value = MAX_INPUT_VALUE;
		update(updatedTiers);
	};

	const handleUpdateInput = (_row, column, location, area, input) => {
		const updatedTiers = [...innerTiers];
		const switchDisabled = Boolean(input === "Dollar");

		switch (location) {
			case "column":
				break;
			case "row":
				for (const tier of updatedTiers) {
					if (switchDisabled) {
						tier["commissions"][column]["payment"]["global"] = false;
					}
					tier["commissions"][column][area] = {
						value: 0,
						input,
					};
				}
				break;
			default:
				break;
		}
		update(updatedTiers);
	};

	const handleUpdateValue = (row, column, location, area, value) => {
		const updatedTiers = [...innerTiers];
		const updatedValue = value ? Number(value) : 0;

		switch (location) {
			case "column":
				updatedTiers.forEach((tier) => {
					tier.commissions?.forEach((commission, key) => {
						if (area in commission && key === column) {
							commission[area]["value"] = updatedValue;
							if (tier.commissions?.[key + 1]) {
								tier.commissions[key + 1]["from"]["value"] = Number(updatedValue + 0.01).toFixed(2);
							}
						}
					});
				});
				break;
			case "row":
				if (area !== "commission") {
					updatedTiers[row][area]["value"] = updatedValue;
					if (updatedTiers?.[row + 1] && area === "to") {
						updatedTiers[row + 1]["from"]["value"] = Number(updatedValue + 0.01).toFixed(2);
					}
				} else {
					updatedTiers[row]["commissions"][column][area]["value"] = updatedValue;
				}
				break;
			default:
				break;
		}

		update(updatedTiers);
	};

	const update = (updatedTiers) => {
		wasUpdated.current = true;
		setInnerTiers(updatedTiers);
	};

	useMemo(() => {
		if (innerTiers?.length) {
			validate([
				{
					definition: {
						useKey: "gross",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<", // If from < to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: "===",
									deltas: { Dollar: 0.01 }, // Add delta on upper value
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerTiers || [],
				},
				{
					definition: {
						useKey: "commissions",
						operators: {
							conditions: [
								{
									order: "COLUMNS",
									type: "STEP",
									on: "value",
									compare: "<=", // If upper <= lower is good,
									deltas: { Percent: 0, Dollar: 0 },
								},
							],
						},
						errorsMap: ["commission"],
						errorsMapOn: "commissions",
						message: "Incorrect commissions definition",
						validationType: "MATRIX",
					},
					section: innerTiers || [],
				},
			]);
		}
	}, [innerTiers, validate]);

	useEffect(() => {
		setInnerTiers(clone(tiers));

		return () => {
			wasUpdated.current = false;
			setInnerTiers([]);
		};
	}, [tiers, setInnerTiers]);

	const headers =
		innerTiers?.[0]?.commissions?.map((item) => {
			const switchDisabled = Boolean(item?.commission?.input === "Dollar");
			return { ...item?.payment, switchDisabled };
		}) || [];

	return (
		<Stack width={1} mt={2}>
			{Boolean(title) && (
				<Typography className="!font-montserrat" variant={"h6"} color="var(--text-color)" paddingTop={"3px"}>
					{title}
				</Typography>
			)}

			<Grid container item xs={12} mt={2.5}>
				<Grid item position={"relative"} xs={12} ml={"87px"}>
					<TierGrossHeader
						headers={headers}
						mapKeys={["payLabel", "payType", "global"]}
						readOnly={readOnly}
						onActionDelete={handleDeleteColumn}
						onActionUpdate={handleAddColumn}
					/>
				</Grid>

				<Grid item xs={12} mt={1} minHeight={"100px"}>
					<TierRows
						showTitle
						title={"Gross"}
						sxTitlePropsContainer={{
							top: "84px",
							left: "30px",
						}}
						centerTitleAfterRow={1}
						deleteButtonText={"Remove Tier"}
						addButtonText={"Add Tier"}
						deleteButtonDisabled={innerTiers?.length <= 1 || readOnly}
						addButtonDisabled={innerTiers?.length >= 50 || readOnly}
						spaces={{
							mainSection: 201,
							columns: 129,
							totalColumns: innerTiers?.[0]?.commissions?.length || 1,
							totalRows: innerTiers?.length || 1,
							separation: 5.5,
						}}
						rowsAreaProps={{
							ml: "35px",
						}}
						onClickDelete={handleDeleteTier}
						onClickAdd={handleAddTier}
					>
						<TransitionGroup>
							<Collapse>
								<Stack direction="column" rowGap={2}>
									{innerTiers?.map((tier, key) => {
										const fromDisabled = Boolean(!key);
										const toDisabled = Boolean(innerTiers?.length === key + 1);
										const commissionsLength = innerTiers?.[0]?.commissions?.length || 0;
										const reduction = ((commissionsLength * 4.65) / commissionsLength) * 3.65;
										const itemKey = `payment_row_${key}`;
										return (
											<Grid key={itemKey} container item>
												<MatrixRow
													index={key}
													item={tier}
													fromDisabled={fromDisabled}
													toDisabled={toDisabled}
													spaces={{
														mainSection: 201,
														columns: 129,
														totalColumns: innerTiers?.[0]?.commissions?.length || 1,
														totalRows: innerTiers?.tiers?.length || 1,
														separation: 5.5,
														reduction,
													}}
													rowsErrors={rowsErrors}
													columnsErrors={columnsErrors}
													readOnly={readOnly}
													spacing={"11px"}
													sxMainContent={{ minWidth: "200.42px" }}
													sxCells={{ minWidth: "129px" }}
													onActionUpdate={handleUpdateValue}
													onChangeType={handleUpdateInput}
												/>
											</Grid>
										);
									})}
								</Stack>
							</Collapse>
						</TransitionGroup>

						{Boolean(localErrorsMessages?.length) && (
							<Grid container direction="column" rowSpacing={0.2} mt={1} alignItems="flex-start">
								{localErrorsMessages.map((message, key) => (
									<TierAlertError key={`error_payments_${message}_${key}`} text={message} />
								))}
							</Grid>
						)}
					</TierRows>
				</Grid>
			</Grid>
		</Stack>
	);
}
TiersPayment.propTypes = {
	title: PropTypes.string,
	tiers: PropTypes.array,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};
